/**
 * Demo Mode Bar
 *
 * 40px bar at the top of the page during demo mode.
 * Shows current persona, persona switch buttons, reset, and exit.
 * Professional appearance — this is a sales tool, not a dev tool.
 */

import { FiRefreshCw, FiX } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useDemoModeStore, DEMO_PERSONAS, getDemoPersona } from '../../store'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Separator } from '../ui/separator'

export function DemoModeBar() {
  const navigate = useNavigate()
  const isActive = useDemoModeStore((s) => s.isActive)
  const activePersonaKey = useDemoModeStore((s) => s.activePersonaKey)
  const switchPersona = useDemoModeStore((s) => s.switchPersona)
  const resetDemoData = useDemoModeStore((s) => s.resetDemoData)
  const exitDemo = useDemoModeStore((s) => s.exitDemo)
  const isTransitioning = useDemoModeStore((s) => s.isTransitioning)

  if (!isActive) return null

  const activePersona = activePersonaKey ? getDemoPersona(activePersonaKey) : null

  return (
    <div className="h-10 shrink-0 bg-gray-900 text-white flex items-center px-3 gap-2 text-sm shadow-md">
        {/* Brand label */}
        <span className="font-bold text-[11px] tracking-widest uppercase text-primary shrink-0">
          Demo
        </span>

        <Separator orientation="vertical" className="h-5 bg-white/20" />

        {/* Desktop: persona buttons */}
        <div className="hidden md:flex items-center gap-1">
          {DEMO_PERSONAS.map((persona) => (
            <button
              key={persona.key}
              onClick={() => switchPersona(persona.key, navigate)}
              disabled={isTransitioning}
              className={cn(
                'px-2.5 py-1 rounded text-[11px] font-medium transition-colors whitespace-nowrap',
                activePersonaKey === persona.key
                  ? 'bg-primary text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              )}
            >
              {persona.label}
            </button>
          ))}
        </div>

        {/* Mobile: select dropdown */}
        <div className="md:hidden flex-1">
          <select
            value={activePersonaKey ?? ''}
            onChange={(e) => switchPersona(e.target.value, navigate)}
            disabled={isTransitioning}
            className="w-full bg-white/10 text-white text-xs rounded px-2 py-1 border border-white/20"
          >
            {DEMO_PERSONAS.map((persona) => (
              <option key={persona.key} value={persona.key} className="bg-gray-900">
                {persona.label}
              </option>
            ))}
          </select>
        </div>

        {/* Spacer */}
        <div className="flex-1 hidden md:block" />

        {/* Current persona label (desktop) */}
        {activePersona && (
          <span className="hidden lg:block text-[10px] text-white/50 shrink-0">
            Viewing as {activePersona.label}
          </span>
        )}

        <Separator orientation="vertical" className="h-5 bg-white/20 hidden md:block" />

        {/* Reset */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px] text-white/70 hover:text-white hover:bg-white/10"
          onClick={resetDemoData}
          disabled={isTransitioning}
        >
          <FiRefreshCw size={12} className="mr-1" />
          <span className="hidden sm:inline">Reset</span>
        </Button>

        {/* Exit */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px] text-white/70 hover:text-white hover:bg-white/10"
          onClick={() => exitDemo(navigate)}
        >
          <FiX size={12} className="mr-1" />
          <span className="hidden sm:inline">Exit</span>
        </Button>
    </div>
  )
}
